import { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { createStore, updateStore } from '../services/project/storeService';

const StoreModal = ({ projectId, store, show, onClose, refetch = () => {} }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const flattenFields = (store) => {
    const values = {
      id: store?.id || '',
    };

    const fields = store?.fields || {};
    for (const [key, value] of Object.entries(fields)) {
      if (key === 'address' && typeof value === 'object') {
        if (value.province) {
          values.provinceId = value.province.id || '';
          values.provinceName = value.province.name || '';
        }
        if (value.city) {
          values.cityId = value.city.id || '';
          values.cityName = value.city.name || '';
        }
        if (value.barangay) {
          values.barangayId = value.barangay.id || '';
          values.barangayName = value.barangay.name || '';
        }
        if (value.street) {
          values.street = value.street;
        }
      } else {
        values[key] = typeof value === 'string' || typeof value === 'number' ? value : JSON.stringify(value);
      }
    }

    return values;
  };

  const formik = useFormik({
    initialValues: flattenFields(store),
    validationSchema: Yup.object({
      id: Yup.string().required('ID is required'),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const {
          id,
          provinceId, provinceName,
          cityId, cityName,
          barangayId, barangayName,
          street,
          ...rest
        } = values;

        const fields = {
          ...rest,
          address: {
            province: { id: provinceId, name: provinceName },
            city: { id: cityId, name: cityName },
            barangay: { id: barangayId, name: barangayName },
            street
          }
        };

        const storeDetails = {
          id,
          projectId: parseInt(projectId),
          fields
        };

        const response = store
          ? await updateStore(projectId, store.id, storeDetails)
          : await createStore(projectId, storeDetails);

        toast.success(response.message || 'Store saved!');
        onClose();
        refetch();
      } catch (error) {
        console.error('Store save error:', error);
        toast.error(error.response?.data?.errorMessage || 'Something went wrong');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const renderInput = (name, label) => (
    <Form.Group className="mb-3" controlId={name} key={name}>
      <Form.Label>{label}</Form.Label>
      <Form.Control
        type="text"
        {...formik.getFieldProps(name)}
        isInvalid={formik.touched[name] && formik.errors[name]}
      />
      <Form.Control.Feedback type="invalid">
        {formik.errors[name]}
      </Form.Control.Feedback>
    </Form.Group>
  );

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{store ? `Edit ${store.id}` : 'New Store'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={formik.handleSubmit}>
          {Object.keys(formik.values).map(key => renderInput(key, key))}
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Submit'}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default StoreModal;
